import React, {SetStateAction, useCallback, useEffect} from "react";
import {stepsKeys, TStep} from "../../../../../uiComponents/StepsControl/StepsControl";
import {TForm2553Information} from "../../CreationControl/types";

import styles from './Preview.module.scss';
import {Accordion, AccordionTab } from "primereact/accordion";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {useQuery} from "@tanstack/react-query";
import {PrivacyService} from "../../../../PolicyPages/PolicyService";
import {TermsTypeKeys} from "../../../../TermsUpdate/utils";
import {ProgressSpinner} from "primereact/progressspinner";
import {Checkbox} from "primereact/checkbox";
import moment from "moment";
import { classNames } from "primereact/utils";
import numberToLocaleString from "../../../../../helpers/numberToLocaleString";

type pageProps = {
    pdfLoaded: boolean;
    companyId: number;
    formId: string;
    companies:any[];
    setIsPdfLoaded:React.Dispatch<SetStateAction<boolean>>;
    previewDocument: any;
    form2553Information: TForm2553Information;
    setStep: React.Dispatch<React.SetStateAction<TStep>>;
}
function PreviewAndTerms(props:pageProps) {
    const {
        form2553Information,
        previewDocument,
        setIsPdfLoaded,
        companyId,
        companies,
        pdfLoaded,
        setStep,
        formId
    } = props;

    const {
        businessInformation,
        shareHolders,
        officerInformation,
        lateElectionJustification
    } = form2553Information;


    const [showModal, setShowModal] = React.useState(false);
    const [isTermsReaded, setIsTermsReaded] = React.useState(false);


    const [isCharged] = React.useState(() => {
        let charged = false
        companies.forEach(company => {
            if (company.companyId === companyId) {
                charged = company.formPricing.FILE_83B.allowedLimit > 0
            }
        })
        return charged;
    });

    const onPreviousStep = () => {
        setStep(stepsKeys.SHAREHOLDER_INFORMATION);
    }

    const { data: termsContent, isFetched } = useQuery({
        gcTime: 0,
        staleTime: 0,
        queryKey: ['terms-for-83b'],
        queryFn: () => PrivacyService.getContentByType(TermsTypeKeys.TERMS_FORM_2553),
    });


    const onNextStep = useCallback(() => {

        if (isTermsReaded) {
            setShowModal(false);
            setStep(stepsKeys.PAY);
        } else {
            setShowModal(true);
        }

    },[isTermsReaded, isCharged]);

    useEffect(() => {
        setIsPdfLoaded(false);
        previewDocument.mutateAsync({
            companyId,
            formId
        }).then(() => {
           setIsPdfLoaded(true);
        });
    }, []);

    return (
        <div className="relative h-full bg-white p-4 ">
            <div className="overflow-y-auto" style={{maxHeight: `calc(100% - 75px)`}}>
                <Accordion multiple>
                    <AccordionTab header="Business Information" headerClassName="bg-white">
                        <div className='w-full block'>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Business Name</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.entityName}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">EIN</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.ein}
                                    </div>
                                </div>
                            </div>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Country</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.address.country}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Address</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.address.line1}
                                    </div>
                                </div>
                            </div>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">City</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.address.city}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">State</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.address.state}
                                    </div>
                                </div>
                            </div>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Zip Code</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.address.zipCode}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Date incorporated</div>
                                    <div className="w-full text-sm font-bold">
                                        {moment(businessInformation.incorporationDate).format('MMMM DD, YYYY')}
                                    </div>
                                </div>
                            </div>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">State of incorporation</div>
                                    <div className="w-full text-sm font-bold">
                                        {businessInformation.stateOfIncorporation}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">S Corp election effective date</div>
                                    <div className="w-full text-sm font-bold">
                                        {moment(businessInformation.electionEffectiveDate).format('MMMM DD, YYYY')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AccordionTab>
                    <AccordionTab header="Officer Information" headerClassName="bg-white">
                        <div className='w-full block'>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Officer Name</div>
                                    <div className="w-full text-sm font-bold">
                                        {officerInformation.name}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Officer Title</div>
                                    <div className="w-full text-sm font-bold">
                                        {officerInformation.title}
                                    </div>
                                </div>
                            </div>
                            <div className="row flex w-full p-2">
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Officer Email</div>
                                    <div className="w-full text-sm font-bold">
                                        {officerInformation.email}
                                    </div>
                                </div>
                                <div className="flex-1 flex flex-column gap-1">
                                    <div className="w-full">Officer Phone Number</div>
                                    <div className="w-full text-sm font-bold">
                                        {officerInformation.phoneNumber}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AccordionTab>
                    <AccordionTab header="Election Information" headerClassName="bg-white">
                        <div className="w-full block">
                            <div className="row w-full flex flex-column gap-1 p-2">
                                <div className="w-full gap-2">Election Type</div>
                                <div className="w-full text-sm font-bold">
                                    {lateElectionJustification ? "Late election" : "Timely election"}
                                </div>
                            </div>
                            {
                                lateElectionJustification ? (
                                    <div className="row w-full p-2">
                                        <div className="flex flex-column gap-1">
                                            <div className="w-full">Explanation of reasons why election was not timely made</div>
                                            <div className="w-full text-sm font-bold">
                                                {lateElectionJustification.justificationText}
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }
                        </div>
                    </AccordionTab>

                    <AccordionTab header="Shareholder Information">
                        {
                            shareHolders.map((shareHolder, index) => {
                                return (
                                    <div key={shareHolder.id} className={classNames("w-full block", {"border-top-1 border-200": index !== 0})}>
                                        <div className="row flex w-full p-2">
                                            <div className="flex-1 flex flex-column gap-1">
                                                <div className="w-full">Shareholder Name</div>
                                                <div className="w-full text-sm font-bold">
                                                    {shareHolder.name}
                                                </div>
                                            </div>
                                            <div className="flex-1 flex flex-column gap-1 text-center">
                                                <div className="w-full">Ownership</div>
                                                <div className="w-full text-sm font-bold">
                                                    {
                                                        shareHolder.numberOfShares ?
                                                         `${numberToLocaleString(shareHolder.numberOfShares)} shares` 
                                                         : `${shareHolder.ownershipPercentage}%`
                                                    }

                                                </div>
                                            </div>
                                        </div>
                                        <div className="row flex w-full p-2">
                                            <div className="flex-1 flex flex-column gap-1">
                                                <div className="w-full">Address</div>
                                                <div className="w-full text-sm font-bold">
                                                    {
                                                        [
                                                            shareHolder.address.line1,
                                                            shareHolder.address.city,
                                                            `${shareHolder.address.state+" "+shareHolder.address.zipCode}`,                                                            
                                                            shareHolder.address.country
                                                        ].join(", ")
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </AccordionTab>
                </Accordion>
            </div>
            <div className="absolute bottom-0 left-0 flex bg-white w-full justify-content-end column-gap-2 pt-4 pb-3 px-4">
                <Button
                    label="Back"
                    type="button"
                    severity="secondary"
                    onClick={onPreviousStep}
                />
                <Button
                    label="Next"
                    type="button"
                    disabled={!pdfLoaded}
                    onClick={onNextStep}
                />
            </div>
            <Dialog 
                    visible={showModal}
                    style={{width:'50vw'}}
                    onHide={() => {
                        setShowModal(false);
                    }}
                    footer={
                        <div className="flex justify-content-between align-items-baseline">
                            <div className="pt-4">
                                <Checkbox checked={isTermsReaded}
                                          value={isTermsReaded}
                                          onChange={() => setIsTermsReaded(!isTermsReaded)}/>
                                <label className="ml-2">
                                    I have read and agree to these terms.
                                </label>
                            </div>
                            <Button type="button"
                                    label="Next"
                                    disabled={!isTermsReaded}
                                    onClick={onNextStep}
                            />
                        </div>
                    }>
                {
                    isFetched ? (
                        <div className="w-full pl-4 pr-4">
                            <div className="content" dangerouslySetInnerHTML={{__html: termsContent?.data?.text}}/>
                        </div>
                    ) : (
                        <div className="w-full h-15rem">
                            <ProgressSpinner style={{ width: '50px', height:'50px' }}  strokeWidth="4"/>
                        </div>
                    )
                }

            </Dialog>
        </div>
    )
}

export default PreviewAndTerms;